import { useCallback, useEffect } from 'react';

import { useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { t as tGlobal } from 'i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { RoutesMap } from '@Router/router.types';
import { useNavigationManager } from '@Router/useNavigationManager';
import { EDD_VERSION } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/index';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { InputRegexMap } from '@Types/types';
import { MAX_CITY_CHARS } from '@Components/inputs/CityInput';
import type { ExtendedUseFormReturn } from '@Hooks/index';
import type { LiliDropdownItem } from '@lili/lili-frontend-common';
import type { Country } from '@Types/country';

export interface AdditionalLocation {
  country: Country | null;
  city: string;
}

export interface AdditionalLocationsFormData {
  [EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_HAS_ADDITIONAL]: boolean;
  [EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS]: AdditionalLocation[];
}

export const useAdditionalLocationsLogic = (countriesData: LiliDropdownItem[]) => {
  const { t } = useTranslation('eddQuestions');
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();

  const { navigateToNextPage } = useNavigationManager();
  const defaultValues: AdditionalLocationsFormData = {
    [EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_HAS_ADDITIONAL]:
      getDefaultValueByQuestionId(
        EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_HAS_ADDITIONAL
      ) || false,
    [EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS]:
      getDefaultValueByQuestionId(EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS) ||
      [],
  };

  const locationSchema = z.object({
    country: z.object({
      value: z.string(),
      title: z.string(),
    }),
    city: z
      .string()
      .min(1, tGlobal('enErrors:required'))
      .min(2, tGlobal('enErrors:address.city.min'))
      .max(MAX_CITY_CHARS, tGlobal('enErrors:address.city.max', { max: MAX_CITY_CHARS }))
      .regex(InputRegexMap.cityUS, tGlobal('enErrors:address.city.invalid')),
  });

  const additionalLocationsSchema = z
    .object({
      [EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_HAS_ADDITIONAL]: z.boolean({
        error: () => tGlobal('enErrors:required'),
      }),
      [EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS]: z.array(locationSchema),
    })
    .superRefine((data, ctx) => {
      // If hasAdditionalLocations is true, require at least one location
      if (
        data?.[EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_HAS_ADDITIONAL] === true &&
        (!data?.[EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS] ||
          data?.[EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS]?.length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: tGlobal('enErrors:required'),
          path: [EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS],
        });
      }
    });

  const formMethods: ExtendedUseFormReturn<AdditionalLocationsFormData> = useFormHook({
    formSchema: additionalLocationsSchema,
    formDefaultValues: defaultValues,
    schemaTitle: 'additionalLocations',
    values: defaultValues,
  });
  const { fields, append, prepend, remove } = useFieldArray({
    control: formMethods.control,
    name: EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS,
  });

  const reactiveHasAdditionalLocations = formMethods.watch(
    EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_HAS_ADDITIONAL
  );

  useEffect(() => {
    if (reactiveHasAdditionalLocations && fields.length === 0) {
      append({ country: null, city: '' });
    }
  }, [reactiveHasAdditionalLocations]);

  const handleSubmit = useCallback(async () => {
    saveEddQuestionToLocalStore({
      question: t(
        'organic.additionalLocations.question',
        "Does your business operate in additional locations beyond [User business's address (city, country)]?"
      ),
      questionId: EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_HAS_ADDITIONAL,
      answer: JSON.stringify(
        formMethods.getValues(EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_HAS_ADDITIONAL)
      ),
    });
    saveEddQuestionToLocalStore({
      question: t(
        'organic.additionalLocations.dynamicTitle',
        'Provide a list of all additional locations where your business operates'
      ),
      questionId: EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS,
      answer: JSON.stringify(
        formMethods.getValues(EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS)
      ),
    });
    await postEddData({
      version: EDD_VERSION,
      results: getEddEmbeddedQuestions(),
    });
    navigateToNextPage(RoutesMap.EDD_ADDITIONAL_BUSINESS_LOCATIONS);
  }, [formMethods, navigateToNextPage]);

  const countries: LiliDropdownItem[] = countriesData;

  const hasAdditionalLocations = formMethods.watch(
    EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_HAS_ADDITIONAL
  );
  return {
    handleSubmit,
    formMethods,
    countries,
    fields,
    append,
    prepend,
    remove,
    hasAdditionalLocations,
    isPostingEddData,
    clearErrors: formMethods.clearErrors,
    trigger: formMethods.trigger,
    reset: formMethods.reset,
  };
};
